import type { BvSite, BvOrganisation, BvContact, BvDma, BvRetailContract } from '../../types/billValidationTypes';
import { RATE_ROWS, rateRowValue } from '../../utils/chargeNames';

const PRIMARY = '#3790C5';
const LOGO_SRC = `${import.meta.env.VITE_API_URL ?? 'http://localhost:3001'}/api/logo/choice-energy`;

// ── Formatters ────────────────────────────────────────────────────────────────
function fmtDate(d: string | undefined | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page: {
    fontFamily: "'Segoe UI', Arial, sans-serif",
    color: '#1a1a2e',
    background: '#fff',
    padding: '0',
  } as React.CSSProperties,

  cover: {
    background: `linear-gradient(135deg, ${PRIMARY} 0%, #1a5a8a 100%)`,
    color: '#fff',
    padding: '60px 48px 48px',
    minHeight: '280px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },

  section: {
    padding: '32px 48px 0',
  },

  sectionLast: {
    padding: '32px 48px 48px',
  },

  h1: {
    fontSize: '26px',
    fontWeight: 700,
    margin: 0,
    letterSpacing: '-0.3px',
  } as React.CSSProperties,

  h2: {
    fontSize: '13px',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '1px',
    color: PRIMARY,
    borderBottom: `2px solid ${PRIMARY}`,
    paddingBottom: '6px',
    marginBottom: '14px',
  },

  h3: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1a1a2e',
    margin: '0 0 8px',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '11px',
    marginBottom: '20px',
  },

  th: {
    background: '#f1f5f9',
    color: '#374151',
    fontWeight: 600,
    padding: '7px 10px',
    textAlign: 'left' as const,
    borderBottom: '1px solid #e2e8f0',
    whiteSpace: 'nowrap' as const,
  },

  thCenter: {
    background: '#f1f5f9',
    color: '#374151',
    fontWeight: 600,
    padding: '7px 10px',
    textAlign: 'center' as const,
    borderBottom: '1px solid #e2e8f0',
    whiteSpace: 'nowrap' as const,
  },

  td: {
    padding: '6px 10px',
    borderBottom: '1px solid #f1f5f9',
    color: '#374151',
    verticalAlign: 'top' as const,
  },

  tdMuted: {
    padding: '6px 10px',
    borderBottom: '1px solid #f1f5f9',
    color: '#9ca3af',
    verticalAlign: 'top' as const,
  },

  badge: (color: string) => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '999px',
    fontSize: '10px',
    fontWeight: 600,
    background: color + '20',
    color: color,
  }),

  contractCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    marginBottom: '20px',
    overflow: 'hidden',
  },

  contractHeader: {
    background: '#f8fafc',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderBottom: '1px solid #e2e8f0',
  },

  rateLabel: {
    padding: '6px 10px',
    borderBottom: '1px solid #f1f5f9',
    color: '#64748b',
    fontWeight: 600,
    fontSize: '11px',
    whiteSpace: 'nowrap' as const,
    background: '#fafafa',
  },
};

// ── Props ─────────────────────────────────────────────────────────────────────
export type ComplianceReportDocumentProps = {
  client: BvOrganisation;
  sites: BvSite[];
  contacts: (BvContact & { client_name?: string })[];
  contracts: (BvRetailContract & { _site?: unknown })[];
  dma: (BvDma & { _site?: unknown })[];
  generatedAt: string;
};

// ── Component ─────────────────────────────────────────────────────────────────
export function ComplianceReportDocument({
  client,
  sites,
  contacts,
  contracts,
  dma,
  generatedAt,
}: ComplianceReportDocumentProps) {

  const clientContacts = contacts.filter(c => c.client_name === client.name || c.client_id === client.id);
  const clientSites    = sites.filter(s => s.client_id === client.id || s.client?.id === client.id);
  const siteIdSet      = new Set(clientSites.map(s => s.id));
  const clientDma      = dma.filter(d => {
    const siteId = (d as unknown as { site_id?: string }).site_id;
    return siteId ? siteIdSet.has(siteId) : false;
  });

  return (
    <div style={S.page}>

      {/* ── Cover ───────────────────────────────────────────────────────── */}
      <div style={S.cover}>
        <img src={LOGO_SRC} alt="Choice Energy" style={{ height: '40px', objectFit: 'contain', objectPosition: 'left' }} />
        <div style={{ flex: 1 }} />
        <div>
          <p style={{ margin: '0 0 6px', fontSize: '12px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '1px' }}>
            Client Contract &amp; Compliance Report
          </p>
          <h1 style={S.h1}>{client.name}</h1>
          {client.abn && <p style={{ margin: '6px 0 0', fontSize: '12px', opacity: 0.75 }}>ABN: {client.abn}</p>}
        </div>
        <div style={{ display: 'flex', gap: '24px', marginTop: '8px', fontSize: '11px', opacity: 0.8 }}>
          <span>Generated: {generatedAt}</span>
          <span>{clientSites.length} Site{clientSites.length !== 1 ? 's' : ''}</span>
          <span>{clientContacts.length} Contact{clientContacts.length !== 1 ? 's' : ''}</span>
          <span>{contracts.length} Contract{contracts.length !== 1 ? 's' : ''}</span>
          {clientDma.length > 0 && <span>{clientDma.length} DMA</span>}
        </div>
      </div>

      {/* ── 1. Client Overview ──────────────────────────────────────────── */}
      <div style={S.section}>
        <h2 style={S.h2}>1. Client Overview</h2>
        <table style={S.table}>
          <tbody>
            <tr>
              <td style={{ ...S.td, fontWeight: 600, width: '160px' }}>Business Name</td>
              <td style={S.td}>{client.name}</td>
              <td style={{ ...S.td, fontWeight: 600, width: '160px' }}>ABN</td>
              <td style={S.td}>{client.abn || '—'}</td>
            </tr>
            <tr>
              <td style={{ ...S.td, fontWeight: 600 }}>Status</td>
              <td style={S.td}>
                <span style={S.badge(client.status === 'active' ? '#16a34a' : client.status === 'lead' ? '#d97706' : PRIMARY)}>
                  {(client.status ?? 'lead').charAt(0).toUpperCase() + (client.status ?? 'lead').slice(1)}
                </span>
              </td>
              <td style={{ ...S.td, fontWeight: 600 }}>Salesforce ID</td>
              <td style={S.td}>{client.sf_account_id || '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── 2. Sites ────────────────────────────────────────────────────── */}
      <div style={S.section}>
        <h2 style={S.h2}>2. Sites / NMIs ({clientSites.length})</h2>
        {clientSites.length === 0 ? (
          <p style={{ fontSize: '12px', color: '#9ca3af' }}>No sites found.</p>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                {['NMI', 'Address', 'Current Retailer', 'Distributor', 'Tariff', 'Region', 'DMA Status', 'Enrolment Date', 'BV Status'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clientSites.map((s) => (
                <tr key={s.id}>
                  <td style={{ ...S.td, fontWeight: 600, fontFamily: 'monospace' }}>{s.nmi || '—'}</td>
                  <td style={S.td}>
                    {[s.addr_street, s.addr_city, s.addr_state, s.addr_postcode].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td style={S.td}>{s.current_retailer || '—'}</td>
                  <td style={S.td}>{s.bv_extn?.network_distributor || '—'}</td>
                  <td style={S.td}>{s.bv_extn?.network_tariff_code || '—'}</td>
                  <td style={S.td}>{s.market_region || s.bv_extn?.market_region || '—'}</td>
                  <td style={S.td}>
                    {s.dma_status
                      ? <span style={S.badge('#0891b2')}>{s.dma_status}</span>
                      : '—'
                    }
                  </td>
                  <td style={S.td}>{fmtDate(s.bv_extn?.enrolment_date)}</td>
                  <td style={S.td}>
                    {s.bv_extn?.bill_val_status
                      ? <span style={S.badge(PRIMARY)}>{s.bv_extn.bill_val_status}</span>
                      : '—'
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── 3. Contacts ─────────────────────────────────────────────────── */}
      <div style={S.section}>
        <h2 style={S.h2}>3. Key Contacts ({clientContacts.length})</h2>
        {clientContacts.length === 0 ? (
          <p style={{ fontSize: '12px', color: '#9ca3af' }}>No contacts found.</p>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                {['Name', 'Role', 'Email', 'Phone'].map(h => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {clientContacts.map((c) => (
                <tr key={c.id}>
                  <td style={S.td}>{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                  <td style={S.td}>
                    {c.role
                      ? <span style={S.badge(PRIMARY)}>{c.role}</span>
                      : '—'
                    }
                  </td>
                  <td style={S.td}>{c.email || '—'}</td>
                  <td style={S.td}>{c.phone || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── 4. Retail Contracts ─────────────────────────────────────────── */}
      <div style={S.section}>
        <h2 style={S.h2}>4. Retail Contracts ({contracts.length})</h2>
        {contracts.length === 0 ? (
          <p style={{ fontSize: '12px', color: '#9ca3af' }}>No retail contracts found.</p>
        ) : contracts.map((c) => {
          const site = c._site as { nmi?: string } | undefined;
          const rates = c.rates ?? [];
          return (
            <div key={c.id} style={S.contractCard} data-pdf-avoid-break="true">
              {/* Contract header */}
              <div style={S.contractHeader}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 700, fontSize: '13px' }}>
                    {c.retailer?.name ?? (c as unknown as { retailer_name?: string }).retailer_name ?? 'Unknown Retailer'}
                  </span>
                  {site?.nmi && (
                    <span style={{ marginLeft: '10px', fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>
                      NMI: {site.nmi}
                    </span>
                  )}
                </div>
                <span style={S.badge(PRIMARY)}>{c.rate_type?.charAt(0).toUpperCase() + (c.rate_type ?? '').slice(1)}</span>
                <span style={S.badge(c.is_current ? '#16a34a' : '#6b7280')}>{c.is_current ? 'Current' : 'Expired'}</span>
                <span style={{ fontSize: '11px', color: '#64748b' }}>
                  {fmtDate(c.contract_start)} → {fmtDate(c.contract_end)}
                </span>
              </div>

              {/* Rate schedule */}
              {rates.length === 0 ? (
                <p style={{ padding: '12px 14px', fontSize: '11px', color: '#9ca3af', margin: 0 }}>No rate periods.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ ...S.table, marginBottom: 0, fontSize: '11px' }}>
                    <thead>
                      <tr>
                        <th style={{ ...S.th, background: '#f8fafc' }}>Rate Type</th>
                        {rates.map((r) => (
                          <th key={r.id} style={{ ...S.thCenter, background: '#f8fafc' }}>
                            <div>{fmtDate(r.period_start)} → {fmtDate(r.period_end)}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {RATE_ROWS.map((row) => {
                        const allDash = rates.every(r => rateRowValue(row, r) === '—');
                        if (allDash) return null;
                        return (
                          <tr key={row.label}>
                            <td style={S.rateLabel}>{row.label}</td>
                            {rates.map((r) => {
                              const val = rateRowValue(row, r);
                              return (
                                <td key={r.id} style={val === '—' ? { ...S.td, textAlign: 'center', color: '#d1d5db' } : { ...S.td, textAlign: 'center', fontWeight: 500 }}>
                                  {val}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── 5. DMA / Metering Provider ──────────────────────────────────── */}
      <div style={S.sectionLast}>
        <h2 style={S.h2}>5. DMA / Metering Provider ({clientDma.length})</h2>
        {clientDma.length === 0 && (
          <p style={{ fontSize: '11px', color: '#94a3b8', margin: '-8px 0 14px' }}>
            No DMA contracts on file for this client.
          </p>
        )}
        {clientDma.length > 0 && (
          <table style={S.table}>
            <thead>
              <tr>
                {['NMI', 'Provider', 'Start Date', 'End Date'].map(h => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {clientDma.map((d) => {
                const dmaSiteId = (d as unknown as { site_id?: string }).site_id;
                const dmaSiteJoined = d._site as { nmi?: string; name?: string } | undefined;
                const dmaSiteLocal = clientSites.find(s => s.id === dmaSiteId);
                const nmi = dmaSiteJoined?.nmi || dmaSiteLocal?.nmi || '—';
                return (
                  <tr key={d.id}>
                    <td style={{ ...S.td, fontFamily: 'monospace' }}>{nmi}</td>
                    <td style={S.td}>{d.dma_provider || '—'}</td>
                    <td style={S.td}>{fmtDate(d.start_date)}</td>
                    <td style={S.td}>{fmtDate(d.end_date)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Footer */}
        <div style={{ marginTop: '40px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#9ca3af' }}>
          <span>Choice Energy — Confidential</span>
          <span>Generated {generatedAt}</span>
        </div>
      </div>

    </div>
  );
}
